import {
    httpGet,
    httpPost,

    // NEW - existing utility
    httpPostFile,

    // NEW - existing utility
    httpDownload
} from '../../../../utils/api.utils';

import {
    setKyberConfig,
    setKyberConfigLoading,
    setKyberConfigSaved,

    // NEW - required by ChatBot Agent actions/reducer
    setKyberChatBotAgentConfig,
    setKyberChatBotAgentLoading,

    setKyberProcAgentConfig,
    setKyberProcAgentLoading,
    setKyberProcAgentLoadFailed,
    setKyberProcAgentSaving,
    setKyberProcAgentSaved,
    setKyberProcAgentSaveFailed
} from '../../../../constants/model-reducers/common/kyberconfig.settings';

export const initialState = {
    isLoading: false,

    // Current Enable Kyber Agents toggle value.
    isEnabled: false,

    // Available roles retrieved from tbl_Roles.
    roleOptions: [],

    // Currently selected roles from tbl_KyberAgentConfigRole.
    selectedRoleIds: [],

    // Indicates whether the last save was successful.
    saveSuccessful: false,

    // Changes after every successful Kyber configuration save.
    lastUpdated: null,

    // ChatBot Agent
    chatBotAgent: {
        agentEnabled: false,
        converseUsingFaq: false,
        articles: []
    },

    // #region Kyber Proc
    isKyberProcAgentLoading: false,
    kyberProcAgentLoadError: null,
    kyberProcAgentConfig: {
        agentEnabled: false,
        smartPickEnabled: false,
        selectedRoleIds: [],
        articles: [],
    },
    isKyberProcAgentSaving: false,
    kyberProcAgentSaveError: null,
    kyberProcAgentSaveResult: null,
    // #endregion
};

export const KyberConfigActions = {

    /**
     * Loads the current Kyber Agent configuration.
     *
     * GET external/kyberconfig/agent
     *
     * Response should contain:
     * - isEnabled
     * - selectedRoleIds
     * - roleOptions
     */
    getKyberAgentConfig: () => async (dispatch) => {
       dispatch({
            type: setKyberConfigLoading,
            payload: true
        });
        
        try {
            const response = await httpGet(
                'external/kyberconfig/agent'
            );

            dispatch({
                type: setKyberConfig,
                payload: response
            });

            return response;
        }
        finally {
            dispatch({
                type: setKyberConfigLoading,
                payload: false
            });
        }
    },

    /**
     * Checks whether the logged-in user is allowed to use
     * the Kyber chatbot.
     */
    isKyberChatbotEnabled: () => async (dispatch) => {

        const response = await httpGet(
            'external/kyberconfig/chatbot-enabled'
        );

        return (
            response?.isEnabled === true ||
            response?.IsEnabled === true
        );
    },

    /**
     * Saves the Kyber Agent toggle and selected RoleIds.
     *
     * POST external/kyberconfig/agent
     */
    saveKyberAgentConfig: (
    isEnabled,
    roleIds
    ) => async (dispatch) => {

        const data = {
            IsEnabled: isEnabled,
            RoleIds: Array.isArray(roleIds)
                ? roleIds.map(Number)
                : []
        };

        const response = await httpPost(
            'external/kyberconfig/agent',
            data
        );

        dispatch({
            type: setKyberConfigSaved,
            payload: response?.Success === true
        });

        if (response?.Success === true) {
            dispatch(
                KyberConfigActions.getKyberAgentConfig()
            );
        }

        return response;
    },

    getKyberChatBotAgentConfig: () => async (dispatch) => {

        dispatch({
            type: setKyberChatBotAgentLoading,
            payload: true
        });

        try {
            const response = await httpGet(
                'external/kyberconfig/chatbot-agent'
            );

            dispatch({
                type: setKyberChatBotAgentConfig,
                payload: response
            });

            return response;
        }
        finally {
            dispatch({
                type: setKyberChatBotAgentLoading,
                payload: false
            });
        }
    },

    // UPDATED - create/update SQL data first
    saveKyberChatBotAgentConfig:
    (
        agentEnabled,
        converseUsingFaq,
        addedArticles,
        articleUpdates,
        deletedArticleIds
    ) => async (dispatch) => {

        const data = {
            AgentEnabled:
                agentEnabled,

            ConverseUsingFaq:
                converseUsingFaq,

            // UPDATED - do not serialize JavaScript File object
            AddedArticles:
                Array.isArray(addedArticles)
                    ? addedArticles.map(x => ({
                        TemporaryKey:
                            x.TemporaryKey,

                        FileName:
                            x.FileName,

                        Status:
                            x.Status
                    }))
                    : [],

            ArticleUpdates:
                Array.isArray(articleUpdates)
                    ? articleUpdates
                    : [],

            DeletedArticleIds:
                Array.isArray(deletedArticleIds)
                    ? deletedArticleIds
                    : []
        };

        const response =
            await httpPost(
                'external/kyberconfig/chatbot-agent',
                data
            );

        /*
        * UPDATED
        *
        * Do NOT reload yet.
        *
        * New physical files still need to be uploaded
        * after SQL returns their generated IDs.
        */

        return response;
    },

    // NEW - uploads physical File after SQL ID has been generated
    uploadKyberChatBotArticle:
    (
        articleId,
        file
    ) => async () => {

        const formData =
            new FormData();

        formData.append(
            'file',
            file
        );

        return await httpPostFile(
            `external/kyberconfig/chatbot-agent/article/${articleId}/upload`,
            formData
        );
    },

    // NEW
    downloadKyberChatBotArticle:
    (
        articleId
    ) => async () => {

        return await httpDownload(
            'external/kyberconfig/chatbot-agent/article/download',
            articleId
        );
    },

    // #region Kyber Proc
    getKyberProcAgentConfig: () => async dispatch => {
        dispatch({
            type: setKyberProcAgentLoading,
            payload: true,
        });

        dispatch({
            type: setKyberProcAgentLoadFailed,
            payload: null,
        });

        try {
            const response = await httpGet(
                'external/kyberconfig/kyber-proc-agent'
            );

            if (!response || response.ok === false) {
                const errorMessage =
                    response?.message ||
                    response?.Message ||
                    response?.statusText ||
                    'Unable to load the Kyber Proc configuration.';

                throw new Error(errorMessage);
            }

            const articles =
                response.articles ??
                response.Articles ??
                [];

            dispatch({
                type: setKyberProcAgentConfig,
                payload: {
                    agentEnabled:
                        response.agentEnabled ??
                        response.AgentEnabled ??
                        false,

                    smartPickEnabled:
                        response.smartPickEnabled ??
                        response.SmartPickEnabled ??
                        false,

                    selectedRoleIds:
                        Array.isArray(
                            response.selectedRoleIds ??
                            response.SelectedRoleIds
                        )
                            ? (
                                response.selectedRoleIds ??
                                response.SelectedRoleIds
                            )
                                .map(Number)
                                .filter(roleId =>
                                    roleId > 0)
                            : [],

                    articles:
                        Array.isArray(articles)
                            ? articles
                            : [],
                },
            });
        } catch (error) {
            const errorMessage =
                error?.message ||
                'Unable to load the Kyber Proc configuration.';

            console.error(
                'Error loading Kyber Proc configuration:',
                error
            );

            dispatch({
                type: setKyberProcAgentLoadFailed,
                payload: errorMessage,
            });
        } finally {
            dispatch({
                type: setKyberProcAgentLoading,
                payload: false,
            });
        }
    },
    saveKyberProcAgent:
        (
            agentEnabled,
            smartPickEnabled,
            selectedRoleIds,
            addedArticles,
            articleUpdates,
            deletedArticleIds
        ) => async dispatch => {
            dispatch({
                type:
                    setKyberProcAgentSaving,
                payload:
                    true,
            });

            dispatch({
                type:
                    setKyberProcAgentSaveFailed,
                payload:
                    null,
            });

            try {
                const data = {
                    AgentEnabled:
                        agentEnabled === true,

                    SmartPickEnabled:
                        smartPickEnabled === true,

                    SelectedRoleIds:
                        Array.isArray(selectedRoleIds)
                            ? selectedRoleIds
                                .map(role =>
                                    Number(
                                        role?.Key ??
                                        role?.key ??
                                        role
                                    )
                                )
                                .filter(roleId =>
                                    roleId > 0)
                            : [],

                    AddedArticles:
                        Array.isArray(addedArticles)
                            ? addedArticles.map(
                                article => ({
                                    TemporaryKey:
                                        Number(
                                            article.TemporaryKey
                                        ),

                                    FileName:
                                        article.FileName,

                                    Status:
                                        article.Status === true,
                                })
                            )
                            : [],

                    ArticleUpdates:
                        Array.isArray(articleUpdates)
                            ? articleUpdates.map(
                                article => ({
                                    KyberProcAgentArticleId:
                                        Number(
                                            article
                                                .KyberProcAgentArticleId
                                        ),

                                    Status:
                                        article.Status === true,
                                })
                            )
                            : [],

                    DeletedArticleIds:
                        Array.isArray(deletedArticleIds)
                            ? deletedArticleIds
                                .map(Number)
                                .filter(
                                    articleId =>
                                        articleId > 0
                                )
                            : [],
                };

                const response =
                    await httpPost(
                        'external/kyberconfig/kyber-proc-agent',
                        data
                    );

                const success =
                    response?.success === true ||
                    response?.Success === true;

                if (!success) {
                    const errorMessage =
                        response?.message ||
                        response?.Message ||
                        response?.error ||
                        'Unable to save the Kyber Proc articles.';

                    throw new Error(
                        errorMessage
                    );
                }

                dispatch({
                    type:
                        setKyberProcAgentSaved,
                    payload:
                        response,
                });

                return response;
            } catch (error) {
                const errorMessage =
                    error?.message ||
                    'Unable to save the Kyber Proc articles.';

                console.error(
                    'Error saving Kyber Proc articles:',
                    error
                );

                dispatch({
                    type:
                        setKyberProcAgentSaveFailed,
                    payload:
                        errorMessage,
                });

                return {
                    Success:
                        false,

                    Message:
                        errorMessage,

                    AddedArticles:
                        [],
                };
            } finally {
                dispatch({
                    type:
                        setKyberProcAgentSaving,
                    payload:
                        false,
                });
            }
        },

    uploadKyberProcArticle:
        (
            articleId,
            file
        ) => async () => {
            if (
                Number(articleId) <= 0 ||
                !file
            ) {
                return {
                    Success: false,
                    Message:
                        'A valid Kyber Proc article ID and file are required.',
                };
            }

            const formData =
                new FormData();

            formData.append(
                'file',
                file
            );

            const response =
                await httpPostFile(
                    `external/kyberconfig/kyber-proc-agent/article/${articleId}/upload`,
                    formData
                );

            if (response?.ok === false) {
                let errorMessage =
                    response.statusText ||
                    'Unable to upload the Kyber Proc article.';

                try {
                    const errorBody =
                        await response.json();

                    errorMessage =
                        errorBody?.message ||
                        errorBody?.Message ||
                        errorMessage;
                } catch {
                    // Keep the available HTTP status text.
                }

                return {
                    Success: false,
                    Message: errorMessage,
                };
            }

            return response;
            },

    downloadKyberProcArticle:
        (
            articleId
        ) => async () => {
            if (Number(articleId) <= 0) {
                return {
                    Success: false,
                    Message:
                        'A valid Kyber Proc article ID is required.',
                };
            }

            const response =
                await httpDownload(
                    'external/kyberconfig/kyber-proc-agent/article/download',
                    Number(articleId)
                );

            return response;
        },
    // #endregion
};

export const reducer = (state, action) => {
    state = state || initialState;

    switch (action.type) {

        case setKyberConfigLoading:
            return {
                ...state,
                isLoading: action.payload
            };

        case setKyberConfig:
            return {
                ...state,

                isEnabled:
                    action.payload?.IsEnabled ?? false,

                roleOptions:
                    action.payload?.RoleOptions ?? [],

                selectedRoleIds:
                    action.payload?.SelectedRoleIds ?? []
            };

        case setKyberConfigSaved:
            return {
                ...state,
                saveSuccessful: action.payload,
                lastUpdated: action.payload === true
                    ? Date.now()
                    : state.lastUpdated
            };

        // NEW
        case setKyberChatBotAgentLoading:
            return {
                ...state,
                isLoading: action.payload
            };

        // NEW
        case setKyberChatBotAgentConfig:
            return {
                ...state,

                chatBotAgent: {
                    agentEnabled:
                        action.payload?.agentEnabled ??
                        action.payload?.AgentEnabled ??
                        false,

                    converseUsingFaq:
                        action.payload?.converseUsingFaq ??
                        action.payload?.ConverseUsingFaq ??
                        false,

                    articles:
                        action.payload?.articles ??
                        action.payload?.Articles ??
                        []
                }
            };

        //Kyber Proc
        case setKyberProcAgentLoading:
            return {
                ...state,
                isKyberProcAgentLoading:
                    action.payload,
            };

        case setKyberProcAgentConfig:
            return {
                ...state,

                kyberProcAgentConfig: {
                    agentEnabled:
                        action.payload?.agentEnabled ??
                        false,

                    smartPickEnabled:
                        action.payload?.smartPickEnabled ??
                        false,

                    selectedRoleIds:
                        action.payload?.selectedRoleIds ??
                        [],

                    articles:
                        action.payload?.articles ??
                        [],
                },

                kyberProcAgentLoadError:
                    null,
            };

        case setKyberProcAgentLoadFailed:
            return {
                ...state,
                kyberProcAgentLoadError:
                    action.payload,
            };
        case setKyberProcAgentSaving:
            return {
                ...state,
                isKyberProcAgentSaving:
                    action.payload,
            };

        case setKyberProcAgentSaved:
            return {
                ...state,
                kyberProcAgentSaveResult:
                    action.payload,
                kyberProcAgentSaveError:
                    null,
            };

        case setKyberProcAgentSaveFailed:
            return {
                ...state,
                kyberProcAgentSaveError:
                    action.payload,
            };

        default:
            return state;
    }
};